import WasmController from "react-lib/frameworks/WasmController";

// Interface
import { State as MainState } from "../../../../../HIS/MainHISInterface";

// Common
import { SetProperty } from "../../common/CommonInterface";

// Serializer
import TreatmentOrderInterfaceSerializer from "issara-sdk/types/TreatmentOrderSerializerForCreate_apps_TRT";
import EncounterSerializer from "issara-sdk/types/EncounterSerializer_core";
import DoctorSerializer from "issara-sdk/types/DoctorSerializer_core";

// API
import TreatmentOrderList from "issara-sdk/apis/TreatmentOrderList_apps_TRT";
import TreatmentOrderTriggerView from "issara-sdk/apis/TreatmentOrderTriggerView_apps_INF";
import UserList from "issara-sdk/apis/UserList_users";
import DivisionList from "issara-sdk/apis/DivisionList_core";
import EncounterDetail from "issara-sdk/apis/EncounterDetail_core";
import DoctorDetail from "issara-sdk/apis/DoctorDetail_core";

export type Props = {

}

export type State = Partial<{
  // seq
  TreatmentOrderInterfaceSequence: Partial<{
    sequenceIndex: "Start" | "Action" | null;
    TreatmentOrderInterfaceList: TreatmentOrderInterfaceSerializer[];
    selectedItems: any[];
    userOptions: any[];
    encounterList: EncounterSerializer[];
    doctorOptions: DoctorSerializer[];
    searchTreatmentOrder: FilterSearchType;
    paginatedItems: PaginatedItemsType;
  }> | null;
}>;

export type FilterSearchType = Partial<{
  hn: string | null;
}>;

type PaginatedItemsType = Partial<{
  items: TreatmentOrderInterfaceSerializer[];
  page: number;
  pageSize: number;
  total: number;
}>;

type PickedState = Partial<
  Pick<MainState, "buttonLoadCheck" | "errorMessage" | "successMessage" | "masterOptions">
>;

export type PickedProps = Partial<Omit<PickedState, "">>;

// Sequence
type SeqState = {
  sequence: "TreatmentOrderInterface";
  restart?: boolean;
  clear?: boolean;
};

export type SaveType = "TRIGGER";

export type TableHeaderType<T> = {
  Header: string;
  accessor: keyof T;
  width?: number;
};

export type AccessorType = Partial<Record<keyof TreatmentOrderInterfaceSerializer, string>>;

export type WidthsType = Partial<Record<keyof TreatmentOrderInterfaceSerializer, number>>;

// Handle Action
type ActionType =
  | { action: "SEARCH"; cardKey: string; page?: number; pageSize?: number }
  | { action: "CLEAR"; cardKey: string }
  | { action: "SAVE"; cardKey: string; type: SaveType; selectedItems: any[] };

type SeqAct = ActionType & SeqState;
type SeqType<K> = K extends { action: string } ? Extract<SeqAct, K> : SeqState;

export type RunSequence = <K extends keyof SeqAct>(params: SeqType<Pick<SeqAct, K>>) => void;

export type SetProp = SetProperty<State & PickedState>;

type CustomExtract<T, U> = T extends object ? (U extends Partial<T> ? T : never) : never;

type Params<A extends ActionType["action"]> = CustomExtract<ActionType, { action: A }>;

export const StateInitial: State & PickedState = {
  // seq
  TreatmentOrderInterfaceSequence: {
    sequenceIndex: null,
  },
  // SnackMessage
  errorMessage: null,
  successMessage: null,
};

export type Event =
  | { message: "RunSequence"; params: {} }
  | { message: "GetMasterData"; params: [] };

export type Data = {
  division?: number;
};

export const DataInitial = {};

export const CARD_NAME = "CardTreatmentOrderInterface";

type Optional<T> = {
  [K in keyof T]: `${typeof CARD_NAME}_${T[K] & string}`;
};

export const ACTIONS = {
  SEARCH: "SEARCH",
  CLEAR: "CLEAR",
  SAVE: "SAVE",
} as const;

export const BTN_ACTS = Object.fromEntries(
  Object.keys(ACTIONS).map((key) => [key, `${CARD_NAME}_${key}`])
) as Optional<typeof ACTIONS>;

const FILTER_INIT: FilterSearchType = {
  hn: "",
};

export const LIST_LIMIT = 20;

const PAGINATED_INIT: PaginatedItemsType = {
  items: [],
  page: 0,
  total: 0,
  pageSize: LIST_LIMIT,
};

type Handler<P = any, R = any> = (
  controller: WasmController<State & PickedState, Event, Data, Props>,
  params: P
) => R;

const Masters = [["division", {}]] as const;

// * Hardcode
export const PaymentStatusOptions = [
  { key: 1, value: 1, text: "รอดำเนินการ" },
  { key: 2, value: 2, text: "พร้อมชำระเงิน" },
  { key: 3, value: 3, text: "ชำระเงินแล้ว" },
  { key: 4, value: 4, text: "ชำระเงินบางส่วน" },
];

export const OrderStatusOptions = [
  { key: 1, value: "APPOINTMENT", text: "เป็นคำสั่งล่วงหน้า" },
  { key: 2, value: "PENDING", text: "กำลังทำคำสั่ง" },
  { key: 3, value: "PERFORMED", text: "ทำคำสั่งเรียบร้อยแล้ว" },
  { key: 4, value: "CANCEL", text: "คำสั่งถูกยกเลิก" },
  { key: 5, value: "DRAFT", text: "คำสั่ง DRAFT" },
  { key: 6, value: "PLANNING", text: "คำสั่งแบบต่อเนื่อง" },
  { key: 7, value: "OFF", text: "หยุดทำคำสั่งแบบต่อเนื่อง" },
];

export const OrderTypeOptions = [
  { key: 1, value: "DIRECT", text: "คำสั่งจากแพทย์โดยตรง" },
  { key: 2, value: "VERBAL", text: "คำสั่งโดยวาจา" },
  { key: 3, value: "TELEPHONE", text: "คำสั่งทางโทรศัพท์" },
];

const userMap: Partial<keyof TreatmentOrderInterfaceSerializer>[] = ["edit_user"];

const doctorMap: Partial<keyof TreatmentOrderInterfaceSerializer>[] = ["edited_doctor"];

/**========================================================================
 **                               START
 *========================================================================**/

export const Start: Handler<SeqState> = async (controller, params) => {
  const state = controller.getState();

  controller.handleEvent({
    message: "GetMasterData",
    params: {
      masters: Masters,
    },
  } as any);

  controller.setState(
    {
      TreatmentOrderInterfaceSequence: {
        ...state.TreatmentOrderInterfaceSequence,
        sequenceIndex: "Action",
        paginatedItems: {
          pageSize: LIST_LIMIT,
          page: 0,
        },
      },
    }
    // * issue 75138
    // () => Action(controller, { ...params, action: "SEARCH", cardKey: CARD_NAME })
  );
};

/**========================================================================
 **                              ACTIONS
 *========================================================================**/

export const Action: Handler<ActionType> = async (controller, params) => {
  const actionHandlers = {
    [ACTIONS.SEARCH]: HandleSearchData,
    [ACTIONS.CLEAR]: HandleClearData,
    [ACTIONS.SAVE]: HandleSaveData,
  };

  const action = params.action;

  if (action && actionHandlers[action]) {
    return actionHandlers[action](controller, params as any);
  }
};

const HandleSearchData: Handler<Params<"SEARCH">> = async (controller, params) => {
  const state = controller.getState();

  const searchTreatmentOrder = state.TreatmentOrderInterfaceSequence?.searchTreatmentOrder;
  const paginatedItems = state.TreatmentOrderInterfaceSequence?.paginatedItems;
  const page = (params?.page ?? paginatedItems?.page) || 0;
  const pageSize = (params?.pageSize ?? paginatedItems?.pageSize) || LIST_LIMIT;

  controller.setState({
    buttonLoadCheck: {
      ...state.buttonLoadCheck,
      [BTN_ACTS.SEARCH]: "LOADING",
    },
  });

  const offset = page * pageSize;

  // * issue 75139
  if (!searchTreatmentOrder?.hn) {
    controller.setState({
      buttonLoadCheck: {
        ...state.buttonLoadCheck,
        [BTN_ACTS.SEARCH]: "ERROR",
      },
      errorMessage: {
        ...state.errorMessage,
        [CARD_NAME]: "กรุณาระบุ HN ก่อนทำรายการ",
      },
    });

    return;
  }

  let data = {
    limit: pageSize,
    offset,
    hn: searchTreatmentOrder?.hn,
  };

  const result = await GetTreatmentOrderInterfaceData(controller, data);

  if (result?.items) {
    const uniqueEncounter = (result.items || [])
      .filter(
        (item: TreatmentOrderInterfaceSerializer, index: number, self: any) =>
          index ===
          self.findIndex((t: TreatmentOrderInterfaceSerializer) => t.encounter === item.encounter)
      )
      .map((item: TreatmentOrderInterfaceSerializer) => item?.encounter);

    const encounterList = await Promise.all(
      uniqueEncounter.map(async (id: number) => await GetEncounterDetail(controller, { id }))
    );

    const allUserIds = (result.items || []).flatMap((item: TreatmentOrderInterfaceSerializer) =>
      userMap.map((field) => item[field]).filter((id) => id !== null)
    );

    const uniqueUserIds: number[] = Array.from(new Set(allUserIds));

    const userOptions = await Promise.all(
      uniqueUserIds.map(async (id: number) => await GetUserList(controller, { id }))
    );

    const allDoctorIds = (result.items || []).flatMap((item: TreatmentOrderInterfaceSerializer) =>
      doctorMap.map((field) => item[field]).filter((id) => id !== null)
    );

    const uniqueDoctorIds: number[] = Array.from(new Set(allDoctorIds));

    const doctorOptions = await Promise.all(
      uniqueDoctorIds.map(async (id: number) => await GetDoctorDetail(controller, { id }))
    );

    controller.setState({
      TreatmentOrderInterfaceSequence: {
        ...state.TreatmentOrderInterfaceSequence,
        TreatmentOrderInterfaceList: result?.items,
        encounterList,
        userOptions,
        doctorOptions,
        paginatedItems: {
          ...paginatedItems,
          page,
          pageSize,
          total: result?.total,
        },
      },
      buttonLoadCheck: {
        ...state.buttonLoadCheck,
        [BTN_ACTS.SEARCH]: "SUCCESS",
      },
    });
  } else {
    controller.setState({
      buttonLoadCheck: {
        ...state.buttonLoadCheck,
        [BTN_ACTS.SEARCH]: "ERROR",
      },
    });
  }
};

const HandleSaveData: Handler<Params<"SAVE">> = async (controller, params) => {
  const state = controller.getState();

  controller.setState({
    buttonLoadCheck: {
      ...state.buttonLoadCheck,
      [`${CARD_NAME}_${params.type}`]: "LOADING",
    },
  });

  const [saveResp, saveErr] = await SaveTreatmentOrderInterface(controller, {
    type: params.type,
    selectedItems: params.selectedItems,
  });

  //! Response ตอน Trigger แล้วได้ "" ทำให้ Skip Condition เลยแสดง Button Status ไม่ถูก
  if (saveResp || saveResp === "") {
    controller.setState({
      buttonLoadCheck: {
        ...state.buttonLoadCheck,
        [`${CARD_NAME}_${params.type}`]: "SUCCESS",
      },
      successMessage: {
        ...state.successMessage,
        [CARD_NAME]: "The system has successfully",
      },
    });

    HandleSearchData(controller, { action: "SEARCH", cardKey: CARD_NAME });
  } else {
    controller.setState({
      buttonLoadCheck: {
        ...state.buttonLoadCheck,
        [`${CARD_NAME}_${params.type}`]: "ERROR",
      },
      errorMessage: {
        ...state.errorMessage,
        [CARD_NAME]: saveErr,
      },
    });
  }
};

const HandleClearData: Handler<Params<"CLEAR">> = async (controller, params) => {
  const state = controller.getState();

  controller.setState({
    TreatmentOrderInterfaceSequence: {
      ...state.TreatmentOrderInterfaceSequence,
      searchTreatmentOrder: { ...FILTER_INIT },
      TreatmentOrderInterfaceList: [],
      encounterList: [],
      userOptions: [],
      doctorOptions: [],
      paginatedItems: {
        ...PAGINATED_INIT,
      },
    },
  });
};

/**========================================================================
 **                               APIs
 *========================================================================**/

const GetTreatmentOrderInterfaceData: Handler = async (controller, params) => {
  const state = controller.getState();

  /* params: 'hn' */
  const [resp, err] = await TreatmentOrderList.list({
    apiToken: controller.apiToken,
    params,
    extra: {
      division: controller.data.division,
    },
  });

  if (err) return null;

  return resp;
};

const GetUserList: Handler = async (controller, params) => {
  const state = controller.getState();

  const [resp, err] = await UserList.list({
    apiToken: controller.apiToken,
    params: {
      pk: params.id,
    },
    extra: {
      division: controller.data.division,
    },
  });

  if (err) return null;

  return resp?.items?.[0];
};

const GetDivisionList: Handler = async (controller, params) => {
  const state = controller.getState();

  const [resp, err] = await DivisionList.list({
    apiToken: controller.apiToken,
    params: {
      limit: 9999,
    },
    extra: {
      division: controller.data.division,
    },
  });

  if (err) return null;

  return resp?.items;
};

const GetDoctorDetail: Handler = async (controller, params) => {
  const state = controller.getState();

  const [resp, err] = await DoctorDetail.retrieve({
    apiToken: controller.apiToken,
    pk: params.id,
    extra: {
      division: controller.data.division,
    },
  });

  if (err) return null;

  return resp;
};

const GetEncounterDetail: Handler = async (controller, params) => {
  const state = controller.getState();

  const [resp, err] = await EncounterDetail.retrieve({
    apiToken: controller.apiToken,
    pk: params.id,
    extra: {
      division: controller.data.division,
    },
  });

  if (err) return null;

  return resp;
};

const SaveTreatmentOrderInterface: Handler = async (controller, params) => {
  const state = controller.getState();

  const [resp, err] = await TreatmentOrderTriggerView.put({
    apiToken: controller.apiToken,
    data: {
      treatment_orders: params?.selectedItems || [],
    },
  });

  if (err) return [null, err];

  return [resp, null];
};

/**========================================================================
 **                               UTILs
 *========================================================================**/

export const accessorList: Partial<Record<keyof TreatmentOrderInterfaceSerializer, string>> = {
  id: "ID",
  specific_type: "Specific Type",
  edited_doctor: "แพทย์ผู้แก้ไข",
  doctor_edit_time: "วันเวลาที่แพทย์แก้ไข",
  order_status: "สถานะของคำสั่ง",
  order_payment_status: "สถานะการชำระเงิน",
  order_type: "ชนิดคำสั่ง",
  order_by_name: "แพทย์ผู้สั่ง",
  order_div: "แผนกที่สั่ง",
  order_time: "วันเวลาที่สั่ง",
  order_receive_by: "ผู้รับคำสั่งแทน",
  order_receive_time: "วันเวลาที่รับคำสั่งแทน",
  order_approve_time: "วันเวลาที่ยืนยันคำสั่งแทน",
  order_perform_div: "แผนกที่ทำ",
  order_perform_by_name: "ผู้ปฏิบัติคำสั่ง",
  order_perform_time: "เวลาเริ่มทำ",
  order_ack_by: "ผู้รับทราบคำสั่ง",
  order_ack_time: "เวลารบทราบคำสั่ง",
  order_ack_div: "แผนกที่รับทราบคำสั่ง",
  order_summary_detail_cache: "Order Summary Detail Cache",
  is_advance: "คำสั่งล่วงหน้า",
  is_out_perform_div: "คำสั่งนอกแผนก",
  out_perform_div: "แผนกที่ทำคำสั่งนอกแผนก",
  progression_cycle: "Progression Cycle",
  emr: "EMR",
  encounter: "Encounter",
  is_seen: "เห็นจาก Notification แล้ว",
  edit_user: "ผู้บันทึก",
  created_utc: "วันที่เริ่มต้นบันทึก",
  edited_utc: "วันที่แก้ไขล่าสุด",
  status_name: "Status",
  approved_by: "Approve By",
  approved_at: "Approved At",
  to_be_performer_name: "ผู้ที่จะทำหัตถการ",
  order_detail: "รายละเอียดคำสั่งหัตถการ",
  equipment_detail: "รายละเอียดอุปกรณ์",
  start: "เวลาเริ่มทำหัตถการ",
  finish: "เวลาสิ้นสุดการทำหัตถการ",
  result: "รายละเอียดผลการทำหัตถการ",
  complications: "อาการแทรกซ้อน",
  is_operation_division: "เป็นการลงผลจากแผนก OR หรือไม่",
  is_technician: "รายการสำหรับทำกายภาพ",
  // created: "Created",
  // created_utc: string | null,
  // edited: "Edited",
  // edited_utc: string | null,
  // pk: any,
  // action: "Action",
  // status: "Status",
  // to_be_performer: "DOCTOR" | "NURSE" | "OTHER",
  // order_perform_by: "Order Perform By",
  // patient: number,
  // patient_hn: "Patient HN",
  // patient_name: "Patient Name",
  // patient_age: "Patient Age",
  // patient_location: "Patient Location",
  // patient_birthday: "Patient Birthdate",
  // order_summary_detail: "Order Summary Detail",
  // diagnosis: "Diagnosis",
  // claim_payload: "Claim Payload", // *
  // note: "Note",
  // skip_pending_task: "Skip Pending Task",
  // nurse_status: "Nurse Status",
  // approved_by_username: "Approved By Username",
  // approved_by_password: "Approved By Password",
  // data_changed: "Data Changed",
  // need_approve: "Need Approve",
  // student: number | null,
  // student_name: "Student",
  // student_user: number | null,
  // approved_by_name: string | null,
  // result_approved_at: "Result Approved At",
  // result_approved_by: "Result Approved By",
  // result_approved_by_name: "Result Approved By",
  // is_advance: boolean,
  // is_out_perform_div: boolean,
  // is_seen: boolean,
  // is_operation_division: boolean,
  // is_technician: boolean,
  // order_by: number | null,
  // emr: number | null,
  // encounter: number | null,
  // edit_user: "Edit User",
};

export const GenerateTableHeader = (
  columnnWidths?: WidthsType
): TableHeaderType<TreatmentOrderInterfaceSerializer>[] => {
  return (Object.entries(accessorList) as [keyof TreatmentOrderInterfaceSerializer, string][]).map(
    ([key, label]) => ({
      Header: label,
      accessor: key,
      ...(columnnWidths && columnnWidths[key] ? { width: columnnWidths[key] } : {}),
    })
  );
};
